// PublicationsCard · Lot M4
// Carte cockpit publications : liste canaux + statuts + URLs + actions.

import { Megaphone, ExternalLink, Play, Pause, Sparkles, CalendarDays } from "lucide-react";

const CHANNEL_STATUS_META = {
  draft:    { label: "Brouillon", dot: "#94A3B8", pill: "bg-black/[0.05] text-black/60" },
  active:   { label: "Actif",     dot: "#22C55E", pill: "bg-emerald-50 text-emerald-700" },
  inactive: { label: "Inactif",   dot: "#64748B", pill: "bg-black/[0.04] text-black/50" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "—"; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const ChannelRow = ({ c, disabled, onActivate, onDeactivate }) => {
  const meta = CHANNEL_STATUS_META[c.status] || CHANNEL_STATUS_META.draft;
  return (
    <li
      className="flex items-center gap-4 rounded-2xl bg-white p-4"
      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
      data-testid={`publications-channel-${c.channel}`}
    >
      <span className="h-[8px] w-[8px] rounded-full flex-shrink-0"
            style={{ background: meta.dot }} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[14.5px] font-medium text-black">
            {c.label || c.channel}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-medium ${meta.pill}`}
                data-testid={`channel-${c.channel}-status`}>
            {meta.label}
          </span>
          {c.external_url && (
            <a href={c.external_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[11.5px] text-black/50 hover:text-black">
              <ExternalLink size={11} /> Ouvrir
            </a>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-black/45">
          {c.status === "active" && c.published_at && (
            <span>En ligne depuis {fmtDate(c.published_at)}</span>
          )}
          {c.status === "inactive" && c.unpublished_at && (
            <span>Retiré le {fmtDate(c.unpublished_at)}</span>
          )}
          {c.external_listing_id && (
            <span>ID {c.external_listing_id}</span>
          )}
          <span className="font-mono text-black/30">{c.channel}</span>
        </div>
      </div>
      <div className="flex flex-shrink-0 gap-1">
        {c.status !== "active" ? (
          <button
            onClick={() => onActivate?.(c.channel)}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-full bg-black px-3 py-1.5 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-30"
            data-testid={`channel-${c.channel}-activate-btn`}
          >
            <Play size={11} /> Publier
          </button>
        ) : (
          <button
            onClick={() => onDeactivate?.(c.channel)}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-black/70 hover:text-black disabled:opacity-30"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
            data-testid={`channel-${c.channel}-deactivate-btn`}
          >
            <Pause size={11} /> Retirer
          </button>
        )}
      </div>
    </li>
  );
};

export default function PublicationsCard({
  publications, mandateStatus, isTerminal,
  onAddChannel, onActivate, onDeactivate,
}) {
  const canOperate = !isTerminal && mandateStatus === "commercializing";
  const channels = publications?.channels || [];
  const firstPub = publications?.first_published_at;

  return (
    <section
      className="rounded-3xl bg-white/70 p-7"
      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
      data-testid="publications-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
            <Megaphone size={16} strokeWidth={1.7} className="text-black/60" />
          </div>
          <div>
            <h2 className="text-[16px] font-semibold tracking-[-0.01em]">
              Publications
            </h2>
            <p className="mt-1 text-[12.5px] text-black/50 max-w-[440px]">
              Suivi des canaux de commercialisation.
              La saisie est manuelle en V1 — les connecteurs arrivent plus tard.
            </p>
          </div>
        </div>
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
          M4
        </span>
      </div>

      {/* First published banner */}
      {firstPub ? (
        <div
          className="mt-5 flex items-center gap-2 rounded-2xl bg-[#C8FF2F]/15 px-4 py-3"
          data-testid="publications-first-published-banner"
        >
          <CalendarDays size={14} className="text-black/70" />
          <span className="text-[12.5px] text-black/70">
            Première mise en ligne le
          </span>
          <span className="text-[12.5px] font-medium text-black">
            {fmtDateTime(firstPub)}
          </span>
          <span className="text-[11px] text-black/40 ml-auto">
            Référence M5
          </span>
        </div>
      ) : (
        <div
          className="mt-5 rounded-2xl bg-black/[0.03] px-4 py-3 text-[12.5px] text-black/50"
          data-testid="publications-no-first-published"
        >
          Aucun canal n&apos;a encore été mis en ligne.
        </div>
      )}

      {/* Canaux */}
      <div className="mt-4">
        {channels.length === 0 ? (
          <div className="rounded-2xl bg-black/[0.02] px-4 py-6 text-center text-[12.5px] text-black/50">
            Aucun canal ajouté pour ce mandat.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="publications-channels-list">
            {channels.map((c) => (
              <ChannelRow
                key={c.channel}
                c={c}
                disabled={!canOperate}
                onActivate={onActivate}
                onDeactivate={onDeactivate}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Action */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="text-[11.5px] text-black/45">
          {canOperate
            ? "Ajoutez un canal ou choisissez-en un dans le catalogue."
            : mandateStatus === "commercializing"
              ? "Publications verrouillées (mandat clôturé)."
              : "Le mandat doit être en commercialisation pour publier."}
        </div>
        <button
          onClick={onAddChannel}
          disabled={!canOperate}
          className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-[#C8FF2F] px-4 py-2 text-[12.5px] font-medium text-black transition-transform hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100"
          data-testid="publications-add-channel-btn"
        >
          <Sparkles size={13} /> Ajouter un canal
        </button>
      </div>
    </section>
  );
}
